"use client";

import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import type { DiagnosisFindingItem } from "@/src/components/DiagnosisFindingsPanel";
import type { InteractiveToolInfo } from "./types";
import { OnboardingEmphasisCtaButton } from "@/src/components/OnboardingWizard/shared/OnboardingEmphasisCtaButton";

export type CheckupFindingsChatCardProps = {
  riskCount: number;
  findings: DiagnosisFindingItem[];
  result?: unknown;
  disabled?: boolean;
  isSubmitting?: boolean;
  onBuildPlanClick: () => void;
  className?: string;
  toolInfo?: InteractiveToolInfo;
};

const HEADLINE_SUFFIX = " risks are quietly costing you.";
const SUBHEADLINE = "All fixable — let's take them one at a time.";
const CTA_LABEL = "Build my plan to fix these";

function hasCheckupFindingsResult(result: unknown): boolean {
  return result != null && result !== "";
}

export function CheckupFindingsChatCard({
  riskCount,
  findings,
  result,
  disabled = false,
  isSubmitting = false,
  onBuildPlanClick,
  className,
  toolInfo,
}: CheckupFindingsChatCardProps) {
  const hasResult = hasCheckupFindingsResult(result);
  const showInteractiveActions = !hasResult;
  const isBusy = disabled || isSubmitting;
  const visibleFindings = findings.slice(0, 3);

  return (
    <div
      className={cn(
        "flex w-full max-w-[361px] flex-col gap-3 overflow-clip rounded-xl bg-background_solid_neutral_primary p-4 md:max-w-[534px]",
        className,
      )}
      data-testid="checkup-findings-chat-card"
      data-tool-name={toolInfo?.toolName}
      data-tool-call-id={toolInfo?.toolCallId}
      data-node-id="96365:13294"
    >
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full flex-col gap-1">
          <p className="m-0">
            <span className="title_xxl text-content_accent3_primary">
              {riskCount}
            </span>
            <span className="title_l text-content_primary">
              {HEADLINE_SUFFIX}
            </span>
          </p>
          <p className="body_medium_m m-0 text-content_secondary">
            {SUBHEADLINE}
          </p>
        </div>

        <div
          className="flex w-full flex-col"
          data-testid="checkup-findings-chat-card-list"
        >
          {visibleFindings.map((finding, index) => (
            <div
              key={finding.id}
              className={cn(
                "flex w-full gap-3 py-3.5",
                index < visibleFindings.length - 1 &&
                  "border-b border-border_inverse_transparent10",
              )}
              data-testid={`checkup-findings-chat-card-row-${index}`}
            >
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-background_accent3_overlay10">
                <Icon
                  name="error"
                  size={24}
                  className="text-content_accent3_primary"
                />
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p className="title_l m-0 text-content_primary">
                  {finding.title}
                </p>
                <p className="body_s m-0 text-content_secondary">
                  {finding.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showInteractiveActions ? (
        <OnboardingEmphasisCtaButton
          testId="checkup-findings-chat-card-cta"
          label={CTA_LABEL}
          disabled={isBusy}
          loading={isSubmitting}
          sizeClassName="button_m"
          className="rounded-lg"
          onClick={(event) => {
            event.stopPropagation();
            onBuildPlanClick();
          }}
        />
      ) : null}
    </div>
  );
}
